import { useState } from "react";

// ─── Constants ─────────────────────────────────────────────────────────────────
const today = new Date();
const MN = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const DN = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// ─── Design tokens (matches HiringPipelineCard palette) ───────────────────────
const C = {
  blue: "#185fa5",
  blueLt: "#E6F1FB",
  blueMid: "#B5D4F4",
  green: "#3b6d11",
  greenLt: "#EAF3DE",
  amber: "#ba7517",
  amberLt: "#FAEEDA",
  red: "#a32d2d",
  redLt: "#FCEBEB",
  violet: "#533ab7",
  violetLt: "#EEEDFE",
  border: "#e5e7eb",
  borderMd: "#d1d5db",
  text1: "#111827",
  text2: "#374151",
  text3: "#6b7280",
  text4: "#9ca3af",
  bg: "#f9fafb",
  surface: "#ffffff",
  surface2: "#f3f4f6",
};

const font = "'DM Sans', 'Nunito', system-ui, sans-serif";
const mono = "'DM Mono', 'Courier New', monospace";

// ─── Slot status styles ────────────────────────────────────────────────────────
const SLOT_META = {
  active: { color: C.green, bg: C.greenLt, label: "Active" },
  pending: { color: C.amber, bg: C.amberLt, label: "Pending" },
  inactive: { color: C.text3, bg: C.surface2, label: "Inactive" },
  booked: { color: C.blue, bg: C.blueLt, label: "Booked" },
  cancelled: { color: C.red, bg: C.redLt, label: "Cancelled" },
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const fmtTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const pad = (n) => String(n).padStart(2, "0");

// ─── Slot row ──────────────────────────────────────────────────────────────────
const SlotRow = ({ s }) => {
  const m = SLOT_META[s.status] || SLOT_META.inactive;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 9px",
        borderRadius: 7,
        background: m.bg,
        marginBottom: 3,
      }}
    >
      <div
        style={{
          width: 4,
          height: 4,
          borderRadius: "50%",
          background: m.color,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: m.color,
            fontFamily: mono,
          }}
        >
          {fmtTime(s.startTime)} – {fmtTime(s.endTime)}
        </div>
        <div style={{ fontSize: 10, color: C.text4, marginTop: 1 }}>
          Job #{s.jobPostId} · Round {s.roundId}
        </div>
      </div>
      <span
        style={{
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: m.color,
          background: C.surface,
          border: `0.5px solid ${m.color}`,
          borderRadius: 4,
          padding: "2px 6px",
          flexShrink: 0,
        }}
      >
        {s.status}
      </span>
    </div>
  );
};

// ─── MiniCalendar ─────────────────────────────────────────────────────────────
export const MiniCalendar = ({ slots = [] }) => {
  const [yr, setYr] = useState(today.getFullYear());
  const [mo, setMo] = useState(today.getMonth());
  const [sel, setSel] = useState(today.getDate());

  const dim = new Date(yr, mo + 1, 0).getDate();
  const fd = new Date(yr, mo, 1).getDay();

  // Build date → slots map for this month
  const slotMap = {};
  slots.forEach((s) => {
    const d = new Date(s.startTime);
    if (d.getFullYear() === yr && d.getMonth() === mo) {
      const k = d.getDate();
      (slotMap[k] = slotMap[k] || []).push(s);
    }
  });

  const cells = [
    ...Array(fd).fill(null),
    ...Array.from({ length: dim }, (_, i) => i + 1),
  ];
  const selSlots = sel ? slotMap[sel] || [] : [];

  const prevMonth = () =>
    mo === 0 ? (setMo(11), setYr((y) => y - 1)) : setMo((m) => m - 1);
  const nextMonth = () =>
    mo === 11 ? (setMo(0), setYr((y) => y + 1)) : setMo((m) => m + 1);

  const NavBtn = ({ label, onClick }) => (
    <button
      onClick={onClick}
      style={{
        width: 24,
        height: 24,
        background: C.surface2,
        border: `0.5px solid ${C.border}`,
        borderRadius: 6,
        cursor: "pointer",
        color: C.text2,
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 0.12s",
        fontFamily: font,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = C.blueLt)}
      onMouseLeave={(e) => (e.currentTarget.style.background = C.surface2)}
    >
      {label}
    </button>
  );

  return (
    <div style={{ fontFamily: font }}>
      {/* ── Month nav ───────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <NavBtn label="‹" onClick={prevMonth} />
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: C.text1,
            letterSpacing: "0.01em",
          }}
        >
          {MN[mo]} {yr}
        </span>
        <NavBtn label="›" onClick={nextMonth} />
      </div>

      {/* ── Day-name headers ─────────────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7,1fr)",
          gap: 2,
          marginBottom: 3,
        }}
      >
        {DN.map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center",
              fontSize: 9,
              fontWeight: 600,
              color: C.text4,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* ── Day cells ────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7,1fr)",
          gap: 2,
        }}
      >
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;

          const isToday =
            day === today.getDate() &&
            mo === today.getMonth() &&
            yr === today.getFullYear();
          const hasSl = !!slotMap[day];
          const isSel = sel === day;
          const slotCount = slotMap[day]?.length || 0;

          return (
            <div
              key={day}
              onClick={() => setSel(isSel ? null : day)}
              title={
                hasSl
                  ? `${slotCount} slot${slotCount > 1 ? "s" : ""}`
                  : undefined
              }
              style={{
                height: 28,
                borderRadius: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                gap: 1,
                position: "relative",
                background: isSel
                  ? C.text1
                  : isToday
                    ? C.blueLt
                    : "transparent",
                border:
                  isToday && !isSel
                    ? `1px solid ${C.blueMid}`
                    : "1px solid transparent",
                transition: "background 0.1s",
              }}
              onMouseEnter={(e) => {
                if (!isSel && !isToday)
                  e.currentTarget.style.background = C.surface2;
              }}
              onMouseLeave={(e) => {
                if (!isSel && !isToday)
                  e.currentTarget.style.background = "transparent";
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: isToday || isSel ? 600 : 400,
                  color: isSel ? "#fff" : isToday ? C.blue : C.text2,
                  lineHeight: 1,
                }}
              >
                {day}
              </span>
              {hasSl && (
                <div
                  style={{
                    width: 3,
                    height: 3,
                    borderRadius: "50%",
                    background: isSel ? "rgba(255,255,255,0.6)" : C.blue,
                    marginTop: 1,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Selected day slots ───────────────────────────────────────────── */}
      {sel && (
        <div
          style={{
            marginTop: 10,
            borderTop: `0.5px solid ${C.border}`,
            paddingTop: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 7,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: C.text1 }}>
              {pad(sel)} {MN[mo]}
            </span>
            {selSlots.length > 0 && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  color: C.blue,
                  background: C.blueLt,
                  padding: "2px 6px",
                  borderRadius: 99,
                  letterSpacing: "0.03em",
                }}
              >
                {selSlots.length} slot{selSlots.length > 1 ? "s" : ""}
              </span>
            )}
          </div>

          {selSlots.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "12px 0",
                gap: 4,
              }}
            >
              <div style={{ fontSize: 18 }}>📭</div>
              <span style={{ fontSize: 11, color: C.text4 }}>
                Nothing scheduled
              </span>
            </div>
          ) : (
            selSlots.map((s) => <SlotRow key={s.slotId} s={s} />)
          )}
        </div>
      )}
    </div>
  );
};

export default MiniCalendar;
